import React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getMockAttempts } from "services";
import { getMockTemplates } from "services/sectionService";
import { formatDate } from "utils/date";
import Session from "utils/Session";
import {
    ALL_FOUR_SECTIONS,
    SECTION_LABELS,
    sectionSetKey,
    hasAllFourSections,
} from "utils/ieltsAccess";

const COLUMNS = [
    { key: "name",      label: "Test Name",  align: "left"   },
    { key: "date",      label: "Date",       align: "left"   },
    { key: "overall",   label: "Overall",    align: "center" },
    { key: "listening", label: "Listening",  align: "center" },
    { key: "reading",   label: "Reading",    align: "center" },
    { key: "writing",   label: "Writing",    align: "center" },
    { key: "speaking",  label: "Speaking",   align: "center" },
    { key: "status",    label: "Status",     align: "center" },
    { key: "action",    label: "Action",     align: "right"  },
];

// ─── Score circle ─────────────────────────────────────────────────────────────

function scoreColors(score) {
    if (score === null || score === undefined) {
        return { bg: "#F3F4F6", color: "#9CA3AF" };
    }
    if (score >= 7) return { bg: "#DBEAFE", color: "#3B82F6" };
    if (score >= 6) return { bg: "#FEF3C7", color: "#F59E0B" };
    return { bg: "#FEE2E2", color: "#EF4444" };
}

function ScoreCircle({ score }) {
    const { bg, color } = scoreColors(score);
    return (
        <Box
            sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                backgroundColor: bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
            }}
        >
            <Typography
                sx={{
                    color,
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    lineHeight: 1,
                }}
            >
                {score !== null && score !== undefined ? score : "—"}
            </Typography>
        </Box>
    );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
    const isCompleted = status === "completed";
    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: isCompleted ? "#D1FAE5" : "#FEE2E2",
                color: isCompleted ? "#059669" : "#DC2626",
                borderRadius: "50px",
                padding: "4px 14px",
                fontFamily: "Mulish, sans-serif",
                fontWeight: 600,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
            }}
        >
            {isCompleted ? "Complete" : "Incomplete"}
        </Box>
    );
}

// ─── Action button ────────────────────────────────────────────────────────────

function ActionButton({ status, onViewReport }) {
    const isCompleted = status === "completed";
    return (
        <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
            onClick={onViewReport}
            disabled={!isCompleted}
            sx={{
                border: "1px solid #D1D5DB",
                color: "#374151",
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.82rem",
                fontFamily: "Mulish, sans-serif",
                padding: "6px 14px",
                whiteSpace: "nowrap",
                "&:hover": {
                    backgroundColor: "#F9FAFB",
                    border: "1px solid #9CA3AF",
                },
                "&.Mui-disabled": {
                    border: "1px solid #E5E7EB",
                    color: "#D1D5DB",
                },
            }}
        >
            View Report
        </Button>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

const asTemplateList = (data) => (Array.isArray(data) ? data : data?.templates || []);

/**
 * Map each template's section set to the card label the dashboard shows for it,
 * so a history row reads with the same serial the user clicked.
 *
 * The two queries here must stay identical to the ones in PracticeByTests and
 * SectionTests — the serial is just the template's position in those results,
 * so a different pageSize or filter would silently renumber the rows.
 */
async function buildTestNameIndex() {
    const [fullTestData, ...sectionData] = await Promise.all([
        getMockTemplates({ mockType: "ielts", pageSize: 30, pageNumber: 1 }),
        ...SECTION_LABELS.map((name) =>
            getMockTemplates({ mockType: "ielts", name, pageSize: 10, pageNumber: 1 })
        ),
    ]);

    const index = new Map();

    asTemplateList(fullTestData)
        .filter(hasAllFourSections)
        .forEach((template, i) => {
            const key = sectionSetKey(template.sections);
            if (key) index.set(key, `Full Test - ${i + 1}`);
        });

    SECTION_LABELS.forEach((label, labelIndex) => {
        asTemplateList(sectionData[labelIndex]).forEach((template, i) => {
            const key = sectionSetKey(template.sections);
            // A full-length template already claimed its key; don't renumber it.
            if (key && !index.has(key)) index.set(key, `${label} Test - ${i + 1}`);
        });
    });

    return index;
}

/**
 * Name an attempt from its own sections. Used when the attempt's template is
 * gone from the template lists, where inventing a serial would be a lie.
 */
function fallbackName(attempt) {
    const names = (attempt.mockSections || [])
        .map((s) => (s?.name || "").toLowerCase())
        .filter(Boolean);

    if (names.length >= 4 && ALL_FOUR_SECTIONS.every((s) => names.includes(s))) {
        return "Full Test";
    }

    if (names.length === 1) {
        return `${names[0].charAt(0).toUpperCase() + names[0].slice(1)} Test`;
    }

    return "Mock Test";
}

function mapAttemptToRow(attempt, nameIndex) {
    const { _id, type, createdAt, finishedAt, summary } = attempt;

    // summary is flat and keyed by lowercase section name — { overall, listening, … }.
    // A sectional attempt only carries the one section it covers.
    const score = (key) => (finishedAt ? (summary?.[key]?.score ?? null) : null);

    const name =
        nameIndex.get(sectionSetKey(attempt.mockSections)) || fallbackName(attempt);

    return {
        id: _id,
        name,
        date: finishedAt ? formatDate(finishedAt) : (createdAt ? formatDate(createdAt) : null),
        overall: score("overall"),
        listening: score("listening"),
        reading: score("reading"),
        writing: score("writing"),
        speaking: score("speaking"),
        status: finishedAt ? "completed" : "inprogress",
        type,
        _id,
    };
}

export default function MockTestHistory({ onViewReport, onContinue }) {
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let isMounted = true;

        (async () => {
            try {
                const [{ mockAttempts = [] }, nameIndex] = await Promise.all([
                    getMockAttempts({
                        user: Session.userId,
                        type: 'ielts',
                        order: 'desc',
                        orderBy: 'createdAt',
                        pageSize: 10,
                        pageNumber: 1,
                        include: 'user,mockSections',
                    }),
                    // Names are cosmetic — a template fetch failure must not empty the table.
                    buildTestNameIndex().catch((error) => {
                        console.error("Failed to build test name index:", error);
                        return new Map();
                    }),
                ]);

                if (isMounted) {
                    setRows(mockAttempts.map((attempt) => mapAttemptToRow(attempt, nameIndex)));
                }
            } catch (error) {
                console.error("Failed to fetch mock test history:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => { isMounted = false; };
    }, []);

    return (
        <Box sx={{ px: "76px", py: "48px", backgroundColor: "#F8FAFC", width: "100%" }}>

            {/* Section heading */}
            <Typography
                sx={{
                    color: "#111827",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 800,
                    fontSize: "1.4rem",
                    mb: "24px",
                }}
            >
                Your Mock Test History
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: '48px' }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <TableContainer
                    sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                        overflow: "hidden",
                    }}
                >
                    <Table>
                        {/* Header */}
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#FFFFFF" }}>
                                {COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        align={col.align}
                                        sx={{
                                            color: "#9CA3AF",
                                            fontFamily: "Mulish, sans-serif",
                                            fontWeight: 500,
                                            fontSize: "0.8rem",
                                            borderBottom: "1px solid #E5E7EB",
                                            padding: "12px 16px",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Body */}
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={COLUMNS.length}
                                        align="center"
                                        sx={{
                                            color: "#9CA3AF",
                                            fontFamily: "Mulish, sans-serif",
                                            fontSize: "0.9rem",
                                            py: "48px",
                                            borderBottom: "none",
                                        }}
                                    >
                                        You have not taken any assessment yet!
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    sx={{
                                        backgroundColor: "#FFFFFF",
                                        "&:last-child td": { borderBottom: "none" },
                                    }}
                                >
                                    {/* Test Name */}
                                    <TableCell
                                        sx={{
                                            color: "#111827",
                                            fontFamily: "Mulish, sans-serif",
                                            fontWeight: 600,
                                            fontSize: "0.88rem",
                                            borderBottom: "1px solid #F3F4F6",
                                            padding: "18px 16px",
                                        }}
                                    >
                                        {row.name}
                                    </TableCell>

                                    {/* Date */}
                                    <TableCell
                                        sx={{
                                            color: "#6B7280",
                                            fontFamily: "Mulish, sans-serif",
                                            fontWeight: 400,
                                            fontSize: "0.88rem",
                                            borderBottom: "1px solid #F3F4F6",
                                            padding: "18px 16px",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {row.date ?? "—"}
                                    </TableCell>

                                    {/* Score columns */}
                                    {["overall", "listening", "reading", "writing", "speaking"].map((key) => (
                                        <TableCell
                                            key={key}
                                            align="center"
                                            sx={{
                                                borderBottom: "1px solid #F3F4F6",
                                                padding: "18px 16px",
                                            }}
                                        >
                                            <ScoreCircle score={row[key]} />
                                        </TableCell>
                                    ))}

                                    {/* Status */}
                                    <TableCell
                                        align="center"
                                        sx={{
                                            borderBottom: "1px solid #F3F4F6",
                                            padding: "18px 16px",
                                        }}
                                    >
                                        <StatusBadge status={row.status} />
                                    </TableCell>

                                    {/* Action */}
                                    <TableCell
                                        align="right"
                                        sx={{
                                            borderBottom: "1px solid #F3F4F6",
                                            padding: "18px 16px",
                                        }}
                                    >
                                        <ActionButton
                                            status={row.status}
                                            onViewReport={() => onViewReport && onViewReport(row)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
